/**
 * DisclosureReturn — where DocuSign sends the seller after the Wisconsin
 * condition report (RECR / VLDR) signing session:
 *
 *     /seller/listings/:houseId/disclosure-return?document_id=<id>&event=<event>
 *
 * On mount it calls `POST /api/houses/{id}/disclosure/reconcile`, which reads
 * the envelope's live status from DocuSign and finishes the report if it was
 * signed (idempotent: the Connect webhook may have got there first). The
 * reconcile payload's `status` is authoritative; DocuSign's `event` only
 * decides what to say when the report is not signed:
 *
 *   - completed               → back to the listing's disclosure step
 *   - signing + signing_complete (or no event)
 *                             → "Recording your signature…", polled briefly,
 *                               then a manual Refresh
 *   - signing / draft + cancel, decline, ttl_expired, …
 *                             → a message saying the report isn't signed,
 *                               and a way back to the report
 *
 * The app has no router: App.js matches the path and renders this inside the
 * auth gate, since reconcile needs the signed-in seller.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DisclosureAPI } from '../../agents/disclosureAgent';
import styles from './DisclosureReturn.module.css';

export const POLL_INTERVAL_MS = 3000;
export const MAX_AUTO_POLLS = 10; // ~30s

const SIGNING_COMPLETE = 'signing_complete';

/** What to tell the seller when DocuSign sent them back without a signature. */
const NOT_SIGNED_COPY = {
  cancel: {
    title: 'The report isn’t signed yet',
    body: 'You left DocuSign before signing. Your answers are saved. Go back to the report and choose “Continue signing in DocuSign” when you’re ready.',
  },
  decline: {
    title: 'You declined to sign',
    body: 'You declined to sign the report in DocuSign, so it was not signed. Go back to the report to review your answers and sign again.',
  },
  ttl_expired: {
    title: 'Your DocuSign session expired',
    body: 'The signing session timed out before you signed. Your answers are saved. Go back to the report and choose “Continue signing in DocuSign”.',
  },
  session_timeout: {
    title: 'Your DocuSign session expired',
    body: 'The signing session timed out before you signed. Your answers are saved. Go back to the report and choose “Continue signing in DocuSign”.',
  },
};

const NOT_SIGNED_FALLBACK = {
  title: 'The report isn’t signed yet',
  body: 'DocuSign didn’t record your signature, so the report is not signed. Go back to the report to try again.',
};

/** Exported for tests: the copy for an unsigned return. */
export const notSignedCopy = (event) => NOT_SIGNED_COPY[event] || NOT_SIGNED_FALLBACK;

/**
 * The house id (path segment), document id and DocuSign event (query) of the
 * return URL. Exported for unit testing.
 */
export function parseDisclosureReturnParams(
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
  search = typeof window !== 'undefined' ? window.location.search : '',
) {
  const match = /\/seller\/listings\/(\d+)\/disclosure-return/.exec(pathname || '');
  const houseId = match ? Number(match[1]) : null;
  const params = new URLSearchParams(search || '');
  const rawDoc = params.get('document_id');
  const docNum = rawDoc != null && rawDoc !== '' ? Number(rawDoc) : NaN;
  return {
    houseId,
    documentId: Number.isFinite(docNum) ? docNum : null,
    event: params.get('event') || null,
  };
}

/**
 * @param {object} props
 * @param {number} [props.houseId] - override (defaults to the URL)
 * @param {string} [props.event] - override (defaults to the URL)
 * @param {(houseId: number) => void} props.onDone - open the listing at its
 *   disclosure step
 */
export default function DisclosureReturn({ houseId: houseIdProp, event: eventProp, onDone } = {}) {
  const parsed = useRef(parseDisclosureReturnParams()).current;
  const houseId = houseIdProp ?? parsed.houseId;
  const event = eventProp ?? parsed.event;
  // Only a finished signing is worth waiting on; any other event means the
  // seller left without signing.
  const expectSignature = !event || event === SIGNING_COMPLETE;

  // 'reconciling' | 'completed' | 'recording' | 'not_signed' | 'error'
  const [phase, setPhase] = useState('reconciling');
  const [errorMessage, setErrorMessage] = useState('');
  const [pollCount, setPollCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // The server's `last_signing_error` on a draft: why the signature didn't
  // count (e.g. the date changed while signing). Null when absent.
  const [signingError, setSigningError] = useState(null);

  const headingRef = useRef(null);
  const cancelledRef = useRef(false);
  const doneRef = useRef(false);

  const goBack = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (typeof onDone === 'function') onDone(houseId);
  }, [onDone, houseId]);

  const runReconcile = useCallback(async () => {
    if (!houseId) {
      setPhase('error');
      setErrorMessage('We could not tell which listing this signature belongs to.');
      return;
    }
    try {
      const payload = await DisclosureAPI.reconcile(houseId);
      if (cancelledRef.current) return;
      const reason = payload?.last_signing_error;
      setSigningError(
        payload?.status === 'draft' && typeof reason === 'string' && reason.trim()
          ? reason.trim()
          : null,
      );
      if (payload?.status === 'completed') {
        setPhase('completed');
      } else if (payload?.status === 'signing' && expectSignature) {
        setPhase('recording');
      } else {
        setPhase('not_signed');
      }
    } catch (err) {
      if (cancelledRef.current) return;
      console.error('Disclosure reconcile failed:', err);
      setPhase('error');
      setErrorMessage(err?.message || 'We couldn’t confirm your signature. Please try again.');
    }
  }, [houseId, expectSignature]);

  useEffect(() => {
    cancelledRef.current = false;
    runReconcile();
    return () => {
      cancelledRef.current = true;
    };
  }, [runReconcile]);

  // DocuSign can take a moment to report the envelope complete: poll briefly.
  useEffect(() => {
    if (phase !== 'recording' || pollCount >= MAX_AUTO_POLLS) return undefined;
    const id = setTimeout(() => {
      setPollCount((n) => n + 1);
      runReconcile();
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(id);
  }, [phase, pollCount, runReconcile]);

  // Signed: back to the report, which now shows the signed version.
  useEffect(() => {
    if (phase === 'completed') goBack();
  }, [phase, goBack]);

  useEffect(() => {
    if (['completed', 'not_signed', 'error'].includes(phase) && headingRef.current) {
      headingRef.current.focus();
    }
  }, [phase]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await runReconcile();
    setRefreshing(false);
  };

  const copy = notSignedCopy(event);

  return (
    <div className={styles.page} data-testid="disclosure-return">
      <div className={styles.card} role="status" aria-live="polite" aria-atomic="true">
        {phase === 'reconciling' && (
          <div className={styles.pending} data-testid="disclosure-return-reconciling">
            <span className={styles.spinner} aria-hidden="true" />
            <h1 className={styles.title}>Checking your signature…</h1>
            <p className={styles.subtitle}>
              Just a moment while we confirm your condition report with DocuSign.
            </p>
          </div>
        )}

        {phase === 'completed' && (
          <div data-testid="disclosure-return-completed">
            <h1 className={`${styles.title} ${styles.titleSuccess}`} ref={headingRef} tabIndex={-1}>
              <span className={styles.icon} aria-hidden="true">✓</span>
              Condition report signed
            </h1>
            <p className={styles.subtitle}>Taking you back to your listing…</p>
            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.primary}`}
                onClick={() => typeof onDone === 'function' && onDone(houseId)}
                data-testid="disclosure-return-back"
              >
                Back to your listing
              </button>
            </div>
          </div>
        )}

        {phase === 'recording' && (
          <div data-testid="disclosure-return-recording">
            <h1 className={styles.title}>
              <span className={`${styles.spinner} ${styles.spinnerInline}`} aria-hidden="true" />
              Recording your signature…
            </h1>
            <p className={styles.subtitle}>
              DocuSign is finishing your condition report. This usually takes a few seconds.
              We&rsquo;ll keep checking.
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.primary}`}
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="disclosure-return-refresh"
              >
                {refreshing ? 'Checking…' : 'Check again'}
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.secondary}`}
                onClick={goBack}
                data-testid="disclosure-return-back"
              >
                Back to your listing
              </button>
            </div>
            {pollCount >= MAX_AUTO_POLLS && (
              <p className={styles.hint} data-testid="disclosure-return-poll-stopped">
                DocuSign hasn&rsquo;t confirmed your signature yet. You can leave this page; your
                report will show as signed once DocuSign confirms it. Choose Check again to
                look now.
              </p>
            )}
          </div>
        )}

        {phase === 'not_signed' && (
          <div data-testid="disclosure-return-not-signed">
            <h1 className={`${styles.title} ${styles.titleWarning}`} ref={headingRef} tabIndex={-1}>
              <span className={styles.icon} aria-hidden="true">!</span>
              {copy.title}
            </h1>
            {signingError && (
              <p className={styles.notice} data-testid="disclosure-return-signing-error">
                {signingError}
              </p>
            )}
            <p className={styles.subtitle}>{copy.body}</p>
            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.primary}`}
                onClick={goBack}
                data-testid="disclosure-return-back"
              >
                Back to the report
              </button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div data-testid="disclosure-return-error">
            <h1 className={`${styles.title} ${styles.titleError}`} ref={headingRef} tabIndex={-1}>
              <span className={styles.icon} aria-hidden="true">!</span>
              We couldn&rsquo;t check your signature
            </h1>
            <p className={styles.subtitle} role="alert">{errorMessage}</p>
            <div className={styles.actions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.primary}`}
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="disclosure-return-retry"
              >
                {refreshing ? 'Trying again…' : 'Try again'}
              </button>
              {houseId != null && (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.secondary}`}
                  onClick={goBack}
                  data-testid="disclosure-return-back"
                >
                  Back to your listing
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
